import { useState, useEffect } from 'react';
import { Shield, CheckCircle2, XCircle, Calendar, Search, Clock, Briefcase, Home, Trash2, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import AdminLayout from '@/components/AdminLayout';
import { adminAPI } from '@/lib/api';
import { formatDate } from '@/lib/dateUtils';
import { useBranchFilter } from '@/components/BranchFilterContext';

export default function AdminWorkPermissions() {
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');
  const [search, setSearch] = useState('');
  const { branch } = useBranchFilter();

  useEffect(() => { fetchPermissions(tab); }, [tab, branch]);

  const fetchPermissions = async (status) => {
    setLoading(true);
    try {
      const res = await adminAPI.getWorkPermissions(status === 'all' ? null : status, 1, 50, branch);
      setPermissions(res.data.records || res.data);
    } catch {
      toast.error('Failed to load');
    } finally {
      setLoading(false);
    }
  };

  const handleStatus = async (id, status) => {
    try {
      await adminAPI.updateWorkPermissionStatus(id, status);
      toast.success(`Permission ${status}`);
      fetchPermissions(tab);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const handleEditHours = async (id, currentHours) => {
    const newH = prompt(`Edit hours (current: ${currentHours}h):`, currentHours);
    if (newH === null) return;
    const h = parseFloat(newH);
    if (!h || h <= 0) { toast.error('Enter valid hours'); return; }
    try {
      await adminAPI.editWorkPermit(id, { hours: h });
      toast.success('Hours updated');
      fetchPermissions(tab);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete work permit for ${name}? This will affect their comp-off balance.`)) return;
    try {
      await adminAPI.deleteWorkPermit(id);
      toast.success('Permit deleted');
      fetchPermissions(tab);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const filtered = permissions.filter(p =>
    p.user_name?.toLowerCase().includes(search.toLowerCase()) || p.user_phone?.includes(search)
  );
  const pendingCount = permissions.filter(p => p.status === 'pending').length;

  const typeLabel = { holiday_work: 'Holiday Work', overtime: 'Overtime', wfh: 'Work from Home' };
  const typeColor = { holiday_work: 'bg-[#dea439]/10 text-[#dea439]', overtime: 'bg-blue-50 text-blue-600', wfh: 'bg-violet-50 text-violet-600' };
  const statusColor = { approved: 'bg-[#026536]/10 text-[#026536]', rejected: 'bg-red-50 text-red-500', pending: 'bg-[#dea439]/10 text-[#dea439]' };

  return (
    <AdminLayout>
      <div className="space-y-6 animate-fade-in">
        <div>
          <h1 data-testid="admin-work-permissions-title" className="text-2xl font-extrabold text-[#1a2529]">Work Permissions</h1>
          <p className="text-sm text-[#1a2529]/50">Manage overtime, holiday work, and WFH requests</p>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="bg-white h-10" data-testid="perm-tabs">
            <TabsTrigger value="all" className="font-medium">All</TabsTrigger>
            <TabsTrigger value="pending" className="font-medium data-[state=active]:text-[#dea439] gap-1">
              Pending
              {pendingCount > 0 && <span className="ml-1 h-5 min-w-[20px] rounded-full bg-[#dea439] text-white text-xs flex items-center justify-center px-1.5">{pendingCount}</span>}
            </TabsTrigger>
            <TabsTrigger value="approved" className="font-medium data-[state=active]:text-[#026536]">Approved</TabsTrigger>
            <TabsTrigger value="rejected" className="font-medium data-[state=active]:text-red-500">Rejected</TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#1a2529]/30" size={16} />
          <Input data-testid="perm-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or phone..." className="pl-10 h-10 bg-white" />
        </div>

        <div className="space-y-3">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="h-8 w-8 border-2 border-[#026536] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="bg-white rounded-xl p-8 text-center shadow-sm">
              <Shield className="mx-auto text-[#1a2529]/15 mb-3" size={40} />
              <p className="font-medium text-[#1a2529]/50">No work permission requests</p>
            </div>
          ) : filtered.map((p, idx) => (
            <div key={p.id} data-testid={`perm-${idx}`} className="bg-white rounded-xl p-4 shadow-sm border border-border/30 animate-slide-up" style={{ animationDelay: `${idx * 30}ms` }}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-[#026536]/10 flex items-center justify-center text-sm font-bold text-[#026536]">
                    {p.user_name?.[0]?.toUpperCase()}
                  </div>
                  <div>
                    <p className="font-semibold text-[#1a2529]">{p.user_name}</p>
                    <p className="text-xs text-[#1a2529]/40">+91 {p.user_phone}</p>
                  </div>
                </div>
                <Badge className={`${statusColor[p.status]} text-xs font-semibold border-0 capitalize`}>{p.status}</Badge>
              </div>
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <Badge className={`${typeColor[p.permission_type]} border-0 text-xs`}>
                  {p.permission_type === 'holiday_work' ? <Briefcase size={10} className="mr-1" /> : p.permission_type === 'wfh' ? <Home size={10} className="mr-1" /> : <Clock size={10} className="mr-1" />}
                  {typeLabel[p.permission_type] || p.permission_type}
                </Badge>
                <div className="flex items-center gap-1 text-xs text-[#1a2529]/60">
                  <Calendar size={12} />
                  {formatDate(p.date)}
                  {p.hours && <span className="ml-1 font-semibold">({p.hours}h)</span>}
                </div>
              </div>
              {p.reason && <p className="text-xs text-[#1a2529]/50 mb-3 italic">"{p.reason}"</p>}
              {p.status === 'pending' && (
                <div className="flex gap-2 mb-2">
                  <Button data-testid={`approve-perm-${idx}`} onClick={() => handleStatus(p.id, 'approved')} size="sm"
                    className="flex-1 h-9 bg-[#026536] hover:bg-[#026536]/90 text-white font-medium">
                    <CheckCircle2 size={14} className="mr-1.5" /> Approve
                  </Button>
                  <Button data-testid={`reject-perm-${idx}`} onClick={() => handleStatus(p.id, 'rejected')} size="sm" variant="outline"
                    className="flex-1 h-9 border-red-200 text-red-500 hover:bg-red-50 font-medium">
                    <XCircle size={14} className="mr-1.5" /> Reject
                  </Button>
                </div>
              )}
              <div className="flex items-center gap-3 pt-2 border-t border-border/10">
                <button data-testid={`edit-perm-hours-${idx}`} onClick={() => handleEditHours(p.id, p.hours)} className="flex items-center gap-1 text-[11px] text-blue-600 font-semibold hover:underline">
                  <Pencil size={11} /> Edit Hours
                </button>
                <button data-testid={`delete-perm-${idx}`} onClick={() => handleDelete(p.id, p.user_name)} className="flex items-center gap-1 text-[11px] text-red-500 font-semibold hover:underline">
                  <Trash2 size={11} /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
}
